/**
 * API服务
 */
import axios from 'axios';
import { API_BASE_URL2, FILE_UPLOAD } from '../config';


const api2 = axios.create({
  baseURL: API_BASE_URL2,
  headers: {
    'Content-Type': 'application/json',
  },
});


// 响应拦截器
api2.interceptors.response.use(
  (response) => {
    if (response && response.data?.code === 401) {
      // 未授权，跳转到登录页
      localStorage.removeItem('token');
      window.location.href = '/login';
    } else
      return response.data;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // 未授权，跳转到登录页
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);


// 请求拦截器 - 添加token
api2.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.token = token;
    }
    // 打印请求信息
    console.log('请求URL:', config.url);
    console.log('请求方法:', config.method);
    console.log('请求头:', config.headers);
    console.log('请求数据:', config.data);
    return config;
  },
  (error) => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);


// 根据文件扩展名检查文件类型
const checkFileTypeByExtension = (extension) => {
  // 确保扩展名格式正确（带点号）
  const ext = extension.startsWith('.') ? extension : `.${extension}`;

  // 检查是否在允许的扩展名列表中
  return FILE_UPLOAD.ALLOWED_TYPES.includes(ext.toLowerCase());
};


// API函数
const apiService = {
  // SSE流会话重连
  sseReconnect: async (messageId) => {
    try {
      const response = await api2.post('/chat/sse/reconnect', { messageId });
      return response;
    } catch (error) {
      console.error('SSE重连失败:', error);
      throw error;
    }
  },

  // 登录
  login: (account, password, smsCode) => {
    return api2.post('/auth/login', { account, password, smsCode });
  },

  // 获取历史记录列表
  getHistoryList: (pageSize, pageNum) => {
    return api2.get('/chat/history', { params: { pageSize, pageNum } });
  },

  // 获取历史记录详情
  getHistoryDetail: (chatlistId, pageSize, pageNum, messageId) => {
    return api2.get('/chat/detail', { params: { chatlistId, pageSize, pageNum, messageId } });
  },

  // 获取分支数据
  getBranchDetail: (chatlistId, fMessageId, branchId, pageSize, pageNum) => {
    return api2.get('/chat/branchdetail', { params: { chatlistId, fMessageId, branchId, pageNum, pageSize } });
  },

  // 停止回答
  stopAnswer: (chatlistId, messageId) => {
    return api2.post('/chat/sse/stop', { chatlistId, messageId });
  },

  // 反馈（点赞/点踩）
  sendFeedback: (chatlistId, messageId, feedback) => {
    return api2.post('/chat/feedback', { chatlistId, messageId, feedback });
  },

  // 获取模型列表
  getModelList: () => {
    return api2.get('/model/list');
  },


  // 获取应用基本信息
  getAppInfo: () => {
    return api2.get('/app/info');
  },

  // 获取知识库文件列表
  getRagFilesList: (pageNum = 1, pageSize = 10, keywords = '') => {
    return api2.post('/ragfile/getRagFilesList', { pageNum, pageSize, keywords });
  },

  // 根据知识库文件id获取会话文件id
  getRagFileId: (datasetId, id, name) => {
    return api2.post('/ragfile/getFileId', { datasetId, id, name });
  },

  // 获取长期记忆配置
  getMemoryConfig: () => {
    return api2.get('/memory/config/get');
  },

  // 保存长期记忆配置
  // enable: int类型，0表示开启，1表示关闭
  saveMemoryConfig: (enable, feature1, feature2, hasGuided) => {
    return api2.post('/memory/config/save', { enable, feature1, feature2, hasGuided });
  },

  // 编辑历史记录
  editHistoryItem: async (chatlistId, name) => {
    return await api2.post('/chat/edithistory', {
      chatlistId,
      name
    });
  },

  // 删除历史记录
  deleteHistoryItem: async (chatlistId) => {
    return await api2.post('/chat/delhistory', {
      chatlistId
    });
  },

  // 发送短信验证码
  sendSmsCode: (account, password) => {
    return api2.post('/auth/sendsms', { account, password });
  },

  // 文件上传
  uploadFile: async (file, onProgress = null, modelId = null) => {
    try {
      // 文件大小验证
      if (file.size > FILE_UPLOAD.MAX_SIZE) {
        throw new Error(`文件大小不能超过 ${FILE_UPLOAD.MAX_SIZE / (1024 * 1024)}MB`);
      }

      // 文件类型验证
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!FILE_UPLOAD.ALLOWED_TYPES.includes(fileExtension)) {
        throw new Error(`不支持的文件类型: ${fileExtension}`);
      }

      // MIME类型验证（如果配置了MIME类型检查）
      if (FILE_UPLOAD.ALLOWED_MIME_TYPES && !FILE_UPLOAD.ALLOWED_MIME_TYPES.includes(file.type)) {
        // 对于没有正确MIME类型的文件，使用扩展名进行二次判断
        const isAllowedByExtension = checkFileTypeByExtension(fileExtension);
        if (!isAllowedByExtension) {
          throw new Error(`不支持的文件格式: ${file.type || fileExtension}`);
        }
        // 如果通过扩展名验证，则允许上传
        console.log(`文件 ${file.name} 的MIME类型 ${file.type} 不在允许列表中，但扩展名 ${fileExtension} 被允许`);
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('modelId', modelId);
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000, // 增加到60秒超时
        // 上传进度回调
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      };

      const response = await api2.post('/common/upload', formData, config);
      // 验证响应数据

      if (!response || !response.data) {
        throw new Error(response.msg);
      }

      return response;
    } catch (error) {
      console.error('文件上传失败:', error);

      // 处理不同类型的错误
      if (error.response) {
        // 服务器响应错误
        const { status, data } = error.response;
        if (status === 413) {
          throw new Error('文件过大，请选择较小的文件');
        } else if (status === 415) {
          throw new Error('不支持的文件类型');
        } else if (status === 400) {
          throw new Error(data?.msg || '请求参数错误');
        } else if (status === 401) {
          throw new Error('未授权，请重新登录');
        } else if (status === 403) {
          throw new Error('没有权限上传文件');
        } else if (status === 500) {
          throw new Error('服务器内部错误，请稍后重试');
        } else if (status === 502 || status === 503 || status === 504) {
          throw new Error('服务器暂时不可用，请稍后重试');
        } else if (data && data.msg) {
          throw new Error(data.msg);
        } else {
          throw new Error(`上传失败 (HTTP ${status})`);
        }
      } else if (error.code === 'ECONNABORTED') {
        // 超时错误
        throw new Error('上传超时，请检查网络连接或选择较小的文件');
      } else if (error.code === 'NETWORK_ERROR') {
        // 网络错误
        throw new Error('网络连接失败，请检查网络设置');
      } else if (error.message) {
        // 自定义错误消息
        throw error;
      } else {
        // 未知错误
        throw new Error('上传失败，请稍后重试');
      }
    }
  },

  // 文件下载
  downloadFile: async (fileId, fileName = null, onProgress = null) => {
    try {
      if (!fileId) {
        throw new Error('文件ID不能为空');
      }

      const config = {
        responseType: 'blob',
        timeout: 120000, // 增加到120秒超时，大文件下载需要更长时间
        // 下载进度回调
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      };

      const response = await api2.get(`${'/common/download/temp'}/${fileId}`, config);

      // 验证响应
      if (!response) {
        throw new Error('服务器响应异常');
      }

      // 检查响应是否为错误信息（某些服务器可能返回JSON错误而不是blob）
      if (response.type === 'application/json') {
        const text = await response.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.msg || '下载失败');
        } catch (parseError) {
          // 如果不是JSON，继续处理为正常文件
        }
      }

      // 创建下载链接
      const blob = new Blob([response]);

      // 检查blob大小
      if (blob.size === 0) {
        throw new Error('文件为空或下载不完整');
      }

      const url = window.URL.createObjectURL(blob);

      // 尝试从响应头获取文件名
      let downloadFileName = fileName;
      if (!downloadFileName) {
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          // 支持多种文件名编码格式
          const fileNameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
          if (fileNameMatch && fileNameMatch[1]) {
            downloadFileName = decodeURIComponent(fileNameMatch[1]);
          }
        }
      }

      // 如果仍然没有文件名，使用默认名称
      if (!downloadFileName) {
        downloadFileName = `download_${fileId}_${Date.now()}`;
      }

      // 创建临时链接并触发下载
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // 延迟清理，确保下载开始
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      return {
        success: true,
        fileName: downloadFileName,
        size: blob.size
      };
    } catch (error) {
      console.error('文件下载失败:', error);

      // 处理不同类型的错误
      if (error.response) {
        const { status, data } = error.response;
        if (status === 404) {
          throw new Error('文件不存在或已过期');
        } else if (status === 403) {
          throw new Error('没有权限下载此文件');
        } else if (status === 401) {
          throw new Error('未授权，请重新登录');
        } else if (status === 413) {
          throw new Error('文件过大，无法下载');
        } else if (status === 429) {
          throw new Error('下载请求过于频繁，请稍后重试');
        } else if (status === 500) {
          throw new Error('服务器内部错误，请稍后重试');
        } else if (status === 502 || status === 503 || status === 504) {
          throw new Error('服务器暂时不可用，请稍后重试');
        } else if (data && typeof data === 'string') {
          throw new Error(data);
        } else {
          throw new Error(`下载失败 (HTTP ${status})`);
        }
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('下载超时，请检查网络连接或稍后重试');
      } else if (error.code === 'NETWORK_ERROR') {
        throw new Error('网络连接失败，请检查网络设置');
      } else if (error.message) {
        throw error;
      } else {
        throw new Error('下载失败，请稍后重试');
      }
    }
  },

  // 批量文件上传
  uploadMultipleFiles: async (files, modelId) => {
    try {
      if (!files || files.length === 0) {
        throw new Error('请选择要上传的文件');
      }

      const uploadPromises = Array.from(files).map(file =>
        apiService.uploadFile(file, null, modelId)
      );

      const results = await Promise.allSettled(uploadPromises);

      const successful = [];
      const failed = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successful.push({
            file: files[index],
            result: result.value
          });
        } else {
          failed.push({
            file: files[index],
            error: result.reason.message || '上传失败'
          });
        }
      });

      return {
        successful,
        failed,
        totalCount: files.length,
        successCount: successful.length,
        failCount: failed.length
      };
    } catch (error) {
      console.error('批量上传失败:', error);
      throw new Error('批量上传失败: ' + error.message);
    }
  },

  // 获取记忆列表
  getMemoryList: (pageSize = 10, pageNum = 1) => {
    return api2.get('/memory/memoryys/list', { params: { pageSize, pageNum } });
  },

  // 手动添加隐式记忆
  addImplicitMemory: (memoryBody) => {
    return api2.post('/memory/memoryys/add', { memoryBody });
  },

  // 编辑记忆
  editMemory: (id, memoryBody) => {
    return api2.post('/memory/memoryys/modify', { id, memoryBody });
  },

  // 删除记忆
  deleteMemory: (id) => {
    return api2.get(`/memory/memoryys/remove/${id}`);
  }
};

export default apiService; 
